import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { replace } from 'react-router-redux';
import { withTranslation } from 'react-i18next';
import {
  Table,
  Progress,
  Icon,
  Loader,
  Dimmer,
  Button,
} from 'semantic-ui-react';
import qs from 'qs';

import { decodeSearch, encodeSearch } from '../../utils/url';
import fileSize from '../../utils/fileSize';
import valueToText from '../../utils/valueToText';
import config from '../../../config';
import { fetchM } from '../../actions/fetchM';
import { flashErrorMessage, flashSuccessMessage } from '../../actions/flash';
import PageNav from '../../components/PageNav';
import Timestamp from '../Timestamp';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';

class OverallBookingReports extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    location: PropTypes.shape({
      pathname: PropTypes.string,
      query: PropTypes.object,
      search: PropTypes.string,
    }).isRequired,
  }
  static mapStateToProps = state => ({
    auth: state.auth,
  });
  static getPropsToState = (props) => {
    const query = decodeSearch(props.location.search);
    const currentPage = parseInt(query.ob_page, 10) || 1;
    return {
      currentPage,
    };
  }
  constructor(props) {
    super(props);
    const state = OverallBookingReports.getPropsToState(props);
    this.state = {
      ...state,
      loading: true,
      count: 0,
      limit: 0,
      offset: 0,
      data: [],
      token: '',
      generating: false,
    };
  }
  componentDidMount() {
    const query = decodeSearch(this.props.location.search);
    this.fetchReports(query);
    this.ticker = setInterval(() => {
      if (this.state.generating && this.state.currentPage === 1) {
        this.fetchReports(query, true);
      }
    }, 1800);
  }
  componentWillReceiveProps(nextProps) {
    const query = decodeSearch(nextProps.location.search);
    const state = OverallBookingReports.getPropsToState(nextProps);
    this.setState({ ...state }, () => this.fetchReports(query));
  }
  componentWillUnmount() {
    if (this.ticker) {
      clearInterval(this.ticker);
    }
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  async fetchReports(q, silent = false) {
    if (!silent) await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    const query = Object.assign({}, q);
    if (query.ob_page) query.page = query.ob_page;
    delete query.ob_page;
    let res;
    try {
      res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/reports/overall/bookings/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      if (!silent) await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const token = res.headers.get('X-Csrf-Token');
      const count = parseInt(res.headers.get('M-Data-count'), 10);
      const limit = parseInt(res.headers.get('M-Data-limit'), 10);
      const offset = parseInt(res.headers.get('M-Data-offset'), 10);
      const data = await res.json();
      let generating = false;
      if (data && data[0]) {
        if (!generating && data[0].statusID === 'new') generating = true;
      }
      // handle token
      await this.setComponentState({
        token,
        count,
        limit,
        offset,
        data,
        generating,
      });
    }
    if (!silent) await this.setLoading(false);
  }
  async generateReport() {
    await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    try {
      await store.dispatch(fetchM(`${config.get('bookApiURL')}/reports/overall/bookings/`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.state.token,
        },
      }));
    } catch (err) {
      await this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: `${t('There was an error with your request. Please try again', { ns: TRANSLATION_FILES.translation })}.` }));
      return;
    }
    await this.setLoading(false);
    store.dispatch(flashSuccessMessage({ header: t('Generating Report', { ns: TRANSLATION_FILES.translation }), message: `${t('Report has been added to the queue', { ns: TRANSLATION_FILES.translation })}.` }));
    const query = decodeSearch(this.props.location.search);
    query.ob_page = 1;
    this.fetchReports(query);
  }
  handleChangePage = (currentPage) => {
    const query = decodeSearch(this.props.location.search);
    query.ob_page = currentPage;
    this.setState({ currentPage }, () => {
      this.replaceQuery(query);
    });
  }
  replaceQuery(query, resetPage = false) {
    const { store } = this.context;
    const { pathname } = this.props.location;
    const queryNew = query;
    if (resetPage) {
      queryNew.ob_page = 1;
    }
    this.fetchReports(queryNew);
    store.dispatch(replace(`${pathname}?${encodeSearch(queryNew)}`));
  }
  progressToPercent = (file) => {
    let percent = 0;
    if (file.progress && file.progress.includes('/')) {
      const data = file.progress.split('/');
      const percentFor = parseInt(data[0], 10);
      const percentOf = parseInt(data[1], 10);
      percent = Math.floor((percentFor / percentOf) * 100);
    }
    return percent;
  }
  handleGenerateReport = (e) => {
    e.preventDefault();
    this.setState({ generating: true }, () => {
      this.generateReport();
    });
  }
  STATUSES = [
    { text: 'Generating', value: 'new' },
    { text: 'Completed', value: 'completed' },
    { text: 'Failed', value: 'failed' },
  ];
  render() {
    const {
      count,
      currentPage,
      limit,
      loading,
      offset,
      data,
      generating,
    } = this.state;
    const { t } = this.props;
    return (
      <div>
        <Dimmer
          inverted
          active={this.state.loading}
        >
          <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
        </Dimmer>
        <Table
          stackable
          selectable
        >
          <Table.Header>
            <Table.Row >
              <Table.HeaderCell colSpan={2}>
                <h3>{t('Overall Booking Reports')}</h3>
              </Table.HeaderCell>
              <Table.HeaderCell colSpan={2} textAlign="right">
                {
                  <Button
                    primary
                    onClick={this.handleGenerateReport}
                    disabled={generating}
                  >
                    {generating ? <span>{t('Generating')} &nbsp;&nbsp;</span> : t('Generate Report')}{generating && <Icon loading={generating} name="spinner" />}
                  </Button>
                }
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Header className="mobile-hide">
            <Table.Row>
              <Table.HeaderCell>{t('Name')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Size')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Date')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Status')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {
              data && data.length > 0 ?
                data.map((file, key) => (
                  <Table.Row key={key}>
                    <Table.Cell width={6}>
                      {file.statusID === 'completed' ?
                        <a
                          href={`${config.get('bookApiURL')}/reports/overall/bookings/${file.id}/${file.name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <b><Icon name="download" /> {file.name}</b>
                        </a>
                        :
                        <span><Icon name="file excel outline" /> {file.name}</span>
                      }
                    </Table.Cell>
                    <Table.Cell collapsing>
                      {file.size ? fileSize(file.size) : '-'}
                    </Table.Cell>
                    <Table.Cell collapsing>
                      <Timestamp timestamp={file.createdAt} format="MMMM Do YYYY, h:mm a" />
                    </Table.Cell>
                    <Table.Cell width={4}>
                      <Progress
                        size="small"
                        percent={this.progressToPercent(file)}
                        progress
                        color={file.statusID === 'new' ? 'blue' : null}
                        success={file.statusID === 'completed'}
                        error={file.statusID === 'failed'}
                        active={file.statusID === 'new'}
                      >
                        {t(valueToText(this.STATUSES, file.statusID), { ns: TRANSLATION_FILES.constants })}
                      </Progress>
                    </Table.Cell>
                  </Table.Row>
                ))
                :
                <Table.Row>
                  <Table.Cell colSpan={4} textAlign="center">
                    {t('No reports found')}.
                  </Table.Cell>
                </Table.Row>
            }
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan={4} textAlign="center">
                <PageNav
                  upward
                  currentPage={currentPage}
                  count={count}
                  limit={limit}
                  offset={offset}
                  dataSize={(data && data.length) || 0}
                  loading={loading}
                  onChange={this.handleChangePage}
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>
    );
  }
}

export default withTranslation(namespaces)(connect(OverallBookingReports.mapStateToProps)(OverallBookingReports));



// WEBPACK FOOTER //
// ./src/components/Reports/OverallBookingReports.jsx